'use client';

import { type Outage } from '@/lib/outages-db';
import { OutageCard } from './OutageCard';
import { useState } from 'react';
import { OutageDetailModal } from './OutageDetailModal';
import { useTheme } from '@/components/ThemeProvider';

interface OutageTimelineProps {
  outages: Outage[];
  loading?: boolean;
}

export function OutageTimeline({ outages, loading = false }: OutageTimelineProps) {
  const { theme } = useTheme();
  const [selectedOutage, setSelectedOutage] = useState<Outage | null>(null);

  // Group outages by year
  const outagesByYear = outages.reduce((acc, outage) => {
    const year = new Date(outage.started_at).getFullYear();
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(outage);
    return acc;
  }, {} as Record<number, Outage[]>);

  const years = Object.keys(outagesByYear)
    .map(Number)
    .sort((a, b) => b - a); // Sort years descending (newest first)

  if (loading) {
    return (
      <div className="space-y-12">
        {[1, 2].map((year) => (
          <div key={year} className="relative">
            {/* Year Header Skeleton */}
            <div
              className="sticky top-0 z-10 backdrop-blur-sm py-4 mb-6 border-b"
              style={{
                backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                borderBottomColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb'
              }}
            >
              <div className="animate-pulse flex items-center gap-3">
                <div className="h-8 bg-accent-muted rounded w-20"></div>
                <div className="h-5 rounded w-32" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
              </div>
            </div>

            {/* Timeline Skeleton */}
            <div className="space-y-4 relative before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-gray-300 dark:before:bg-cream/20 pl-8">
              {[1, 2, 3].map((card) => (
                <div key={card} className="relative">
                  {/* Timeline Dot Skeleton */}
                  <div
                    className="absolute left-[-2rem] top-6 w-3 h-3 rounded-full animate-pulse"
                    style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.2)' : '#d1d5db' }}
                  ></div>

                  {/* Card Skeleton */}
                  <div
                    className="border rounded-lg p-6 animate-pulse"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : 'white',
                      borderColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb'
                    }}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="h-6 rounded w-3/4 mb-2" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                        <div className="h-4 rounded w-32" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                      </div>
                      <div className="h-6 rounded-full w-20" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                    </div>

                    {/* Description */}
                    <div className="space-y-2 mb-4">
                      <div className="h-4 rounded w-full" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                      <div className="h-4 rounded w-5/6" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                      <div className="h-4 rounded w-4/6" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                    </div>

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {[1, 2, 3, 4].map((meta) => (
                        <div key={meta}>
                          <div className="h-3 rounded w-16 mb-1" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                          <div className="h-4 rounded w-24" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                        </div>
                      ))}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 4].map((tag) => (
                        <div key={tag} className="h-6 rounded-full w-16" style={{ backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb' }}></div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (outages.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">📊</div>
        <h3 style={{ color: theme === 'dark' ? '#E8D5B7' : '#111827' }} className="text-xl font-semibold mb-2">No outages found</h3>
        <p style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.6)' : '#4b5563' }}>Try adjusting your filters to see more results.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-12">
        {years.map((year) => (
          <div key={year} className="relative">
            {/* Year Header */}
            <div
              className="sticky top-0 z-10 backdrop-blur-sm py-4 mb-6 border-b"
              style={{
                backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                borderBottomColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb'
              }}
            >
              <h2 className="text-2xl font-bold flex items-center gap-3" style={{ color: theme === 'dark' ? '#E8D5B7' : '#111827' }}>
                <span className="text-accent">{year}</span>
                <span className="text-sm font-normal" style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.4)' : '#6b7280' }}>
                  {outagesByYear[year].length} outage{outagesByYear[year].length !== 1 ? 's' : ''}
                </span>
              </h2>
            </div>

            {/* Timeline */}
            <div className="space-y-4 relative before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-gray-300 dark:before:bg-cream/20 pl-8">
              {outagesByYear[year].map((outage, index) => (
                <div key={outage.id} className="relative">
                  {/* Timeline Dot */}
                  <div
                    className={`absolute left-[-2rem] top-6 w-3 h-3 rounded-full border-2 ${
                      outage.severity === 'critical'
                        ? 'bg-red-500 border-red-500'
                        : outage.severity === 'major'
                        ? 'bg-accent border-accent'
                        : 'bg-yellow-500 border-yellow-500'
                    }`}
                  ></div>

                  {/* Outage Card */}
                  <OutageCard outage={outage} onClick={() => setSelectedOutage(outage)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedOutage && (
        <OutageDetailModal outage={selectedOutage} onClose={() => setSelectedOutage(null)} />
      )}
    </>
  );
}
